'use client'

import { captureProductEvent, PRODUCT_EVENTS } from '@/lib/product-analytics'
import { optionalVideoFieldsUsed, videoFunnelEnvelope } from '@/components/santa-video/video-analytics'
import { useState } from 'react'
import type { OrderFormValues } from '@/components/santa-video/order-form-body'
import { useOnBfcacheRestore, useRetryLock } from '@/components/santa-video/order-form-lifecycle'
import { requestCheckout } from '@/components/santa-video/request-checkout'
import type { VideoLocale } from '@/server/domain/santa-video/locale'
import type { VideoStoryId } from '@/server/domain/santa-video/stories'

/**
 * El envío del pedido, fuera del wizard: el doble clic, el `Retry-After` de un
 * 429 y la vuelta del bfcache desde Stripe. Con `checkoutUrl` se navega —el
 * borrador se queda: lo borra la entrega (`ClearOrderDraft`), para que cancelar
 * en Stripe devuelva el formulario relleno—; si no, se enseña el `message`.
 */
export function useCheckoutSubmit({ locale, story, networkError, navigate }: {
  locale: VideoLocale
  story: VideoStoryId
  networkError: string
  navigate: (url: string) => void
}) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const retryLock = useRetryLock()
  useOnBfcacheRestore(() => setSubmitting(false))

  async function submit(values: OrderFormValues) {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    const envelope = videoFunnelEnvelope(story, locale)
    captureProductEvent(PRODUCT_EVENTS.VIDEO_FORM_SUBMITTED, { ...envelope, optional_fields_used: optionalVideoFieldsUsed(values, story) })
    const properties = { ...envelope, provider: 'stripe' }
    const result = await requestCheckout(values, locale, networkError, story)
    if ('checkoutUrl' in result) {
      captureProductEvent(PRODUCT_EVENTS.CHECKOUT_CREATED, properties)
      captureProductEvent(PRODUCT_EVENTS.CHECKOUT_REDIRECTED, properties)
      return navigate(result.checkoutUrl)
    }
    captureProductEvent(PRODUCT_EVENTS.CHECKOUT_FAILED, { ...properties, failure_stage: 'checkout_creation', reason_code: result.reasonCode })
    setError(result.message)
    retryLock.lockFor(result.retryAfterSeconds)
    setSubmitting(false)
  }

  return { submit, submitting, error, locked: retryLock.locked }
}
